import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Briefcase, CheckCircle, Clock, ChevronRight, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { coursesAPI } from '../services/api';
import api from '../services/api';

const Dashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [internships, setInternships] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingInternships, setLoadingInternships] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    coursesAPI.getPurchased()
      .then(res => setCourses(res.data.data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoadingCourses(false));

    api.get('/internships/user/enrolled')
      .then(res => setInternships(res.data.internships || []))
      .catch(() => setInternships([]))
      .finally(() => setLoadingInternships(false));
  }, [isAuthenticated, navigate]);

  const completedInternships = internships.filter(i => i.completion_date);

  return (
    <div className="pt-28 pb-16 px-4 max-w-6xl mx-auto bg-background min-h-screen">
      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-earth-900 mb-1">
          Welcome back, {user?.first_name} 🙏
        </h1>
        <p className="text-earth-500">Here's your learning progress at a glance.</p>
      </motion.div>

      {/* Stats row */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
      >
        {[
          { label: 'Courses Enrolled', value: loadingCourses ? '…' : courses.length, icon: BookOpen, color: 'text-saffron-600' },
          { label: 'Internships Enrolled', value: loadingInternships ? '…' : internships.length, icon: Briefcase, color: 'text-earth-600' },
          { label: 'Internships Completed', value: loadingInternships ? '…' : completedInternships.length, icon: Award, color: 'text-green-600' },
          { label: 'Total Programmes', value: (loadingCourses || loadingInternships) ? '…' : courses.length + internships.length, icon: CheckCircle, color: 'text-terracotta-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-card border border-earth-200 rounded-2xl p-5 flex items-center gap-4">
            <Icon className={`w-8 h-8 flex-shrink-0 ${color}`} />
            <div>
              <p className="text-2xl font-bold text-earth-900">{value}</p>
              <p className="text-xs text-earth-500">{label}</p>
            </div>
          </div>
        ))}
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Enrolled Courses */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="bg-card border border-earth-200 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-serif font-bold text-earth-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-saffron-600" /> My Courses
            </h2>
            <Link to="/" className="text-sm text-saffron-600 hover:underline">Browse more</Link>
          </div>

          {loadingCourses ? (
            <div className="space-y-3">
              {Array(2).fill(0).map((_, i) => (
                <div key={i} className="h-16 bg-earth-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-earth-400 mb-3">No courses enrolled yet.</p>
              <Link to="/" className="px-4 py-2 rounded-lg bg-saffron-600 text-white text-sm font-medium hover:bg-saffron-700 transition-colors">
                Explore Courses
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {courses.map(course => (
                <Link
                  key={course.id}
                  to={`/course/${course.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-earth-50 border border-earth-100 hover:border-saffron-300 transition-colors group"
                >
                  <img
                    src={course.image_url}
                    alt={course.title}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    onError={e => { e.target.src = 'https://via.placeholder.com/100x100/F5A623/FFFFFF?text=C'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-earth-900 text-sm truncate group-hover:text-saffron-600 transition-colors">{course.title}</p>
                    <p className="text-xs text-earth-400">{course.category}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-earth-300 group-hover:text-saffron-500 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </motion.div>

        {/* Enrolled Internships */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="bg-card border border-earth-200 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-serif font-bold text-earth-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-earth-600" /> My Internships
            </h2>
            <Link to="/internships" className="text-sm text-saffron-600 hover:underline">Browse more</Link>
          </div>

          {loadingInternships ? (
            <div className="space-y-3">
              {Array(2).fill(0).map((_, i) => (
                <div key={i} className="h-20 bg-earth-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : internships.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-earth-400 mb-3">No internship projects enrolled yet.</p>
              <Link to="/internships" className="px-4 py-2 rounded-lg bg-saffron-600 text-white text-sm font-medium hover:bg-saffron-700 transition-colors">
                Explore Internships
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {internships.map(item => {
                const progress = Math.round(((item.completed_tasks || 0) / 5) * 100);
                const isComplete = !!item.completion_date;
                return (
                  <Link
                    key={item.id}
                    to={`/internships/${item.project_id}/workspace`}
                    className="block p-3 rounded-xl bg-earth-50 border border-earth-100 hover:border-saffron-300 transition-colors group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-earth-900 text-sm group-hover:text-saffron-600 transition-colors truncate max-w-[200px]">
                        {item.title}
                      </p>
                      {isComplete ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle className="w-3.5 h-3.5" /> Completed
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-earth-400">
                          <Clock className="w-3.5 h-3.5" /> {item.completed_tasks || 0}/5 tasks
                        </span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 bg-earth-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-saffron-500'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-earth-400 mt-1">{item.category}</p>
                  </Link>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>

      {/* Completed internships with certificates */}
      {completedInternships.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-8 bg-card border border-earth-200 rounded-2xl p-6"
        >
          <h2 className="text-xl font-serif font-bold text-earth-900 flex items-center gap-2 mb-5">
            <Award className="w-5 h-5 text-saffron-600" /> Certificates Earned
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {completedInternships.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-4 rounded-xl bg-earth-50 border border-earth-100">
                <Award className="w-8 h-8 text-saffron-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-earth-900 text-sm truncate">{item.title}</p>
                  <p className="text-xs text-earth-400">Completed {new Date(item.completion_date).toLocaleDateString('en-IN')}</p>
                </div>
                <a
                  href={`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/internships/${item.project_id}/certificate`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="text-xs px-3 py-1.5 rounded-lg bg-saffron-600 text-white hover:bg-saffron-700 transition-colors flex-shrink-0"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
